"use client";

import { ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Pagination, PaginationContent, PaginationItem } from "@/components/ui/pagination";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const categories = [{
  label: "Semua",
  value: "all"
}, {
  label: "Nasional",
  value: "nasional"
}, {
  label: "Politik",
  value: "politik"
}, {
  label: "Hukum",
  value: "hukum"
}, {
  label: "Daerah",
  value: "daerah"
}];
const aspectRatios = ["aspect-[3/4]", "aspect-[4/3]", "aspect-square", "aspect-[4/5]"];
function formatEditorialDate(dateString?: string): string {
  if (!dateString) return "— — —";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "— — —";
  const day = String(d.getDate()).padStart(2, "0");
  const months = ["JAN", "FEB", "MAR", "APR", "MEI", "JUN", "JUL", "AGU", "SEP", "OKT", "NOV", "DES"];
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  return `${day} — ${month} — ${year}`;
}
function stripHtml(html?: string): string {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").trim();
}
function JournalConsumer() {
  const {
    posts,
    loading,
    error,
    total,
    currentPage,
    totalPages,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    isRefetching
  } = usePosts();
  if (loading) {
    return <div className="columns-1 md:columns-2 lg:columns-3 gap-10">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="mb-10 break-inside-avoid">
            <div className="aspect-[4/5] bg-muted/40 animate-pulse rounded-lg mb-4" />
            <div className="h-3 w-24 bg-muted/40 animate-pulse mb-3" />
            <div className="h-6 w-full bg-muted/40 animate-pulse mb-2" />
            <div className="h-4 w-3/4 bg-muted/40 animate-pulse" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-sm font-default text-muted-foreground py-12 text-center">
        Gagal memuat berita terkait.
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-sm font-default text-muted-foreground py-12 text-center">
        Tidak ada berita untuk ditampilkan.
      </div>;
  }
  return <>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-16 pb-6 border-b border-border">
        <span className="font-default text-xs tracking-widest text-muted-foreground uppercase">
          BERITA TERKAIT — {String(total || posts.length).padStart(2, "0")}
        </span>
        <div className="flex flex-wrap gap-2">
          {categories.map((cat, i) => <Button key={cat.value} data-index={i} variant={i === 0 ? "default" : "ghost"} size="sm" className={i === 0 ? "rounded-lg font-default text-xs tracking-widest uppercase bg-orange-600 text-white hover:bg-orange-700" : "rounded-lg font-default text-xs tracking-widest uppercase text-black hover:bg-orange-50 hover:text-orange-700"}>
              {cat.label}
            </Button>)}
        </div>
      </div>

      <div className="columns-1 md:columns-2 lg:columns-3 gap-10">
        {posts.map((post, i) => {
        const isNoteCard = !post.featuredImage && i % 5 === 4;
        const aspect = aspectRatios[i % aspectRatios.length];
        const categoryName = post.categories && post.categories.length > 0 ? post.categories[0].name : "Berita";
        const indexLabel = String(i + 1 + (currentPage - 1) * posts.length).padStart(2, "0");
        const dek = stripHtml(post.excerpt);
        if (isNoteCard) {
          return <article key={post.id} data-index={i} className="mb-10 break-inside-avoid bg-white border border-border p-8 rounded-lg group shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <span className="font-default text-xs text-muted-foreground tracking-widest">
                    — {indexLabel}
                  </span>
                  <span className="font-default text-[10px] tracking-widest uppercase text-orange-600">
                    {categoryName}
                  </span>
                </div>
                <p className="font-default text-xs tracking-widest text-muted-foreground mb-4 uppercase">
                  {formatEditorialDate(post.date)}
                </p>
                <h3 className="font-default font-semibold text-2xl md:text-[28px] leading-tight text-black mb-3 transition-colors duration-500 group-hover:text-orange-600">
                  <Link to={post.link || "#"}>{post.title}</Link>
                </h3>
                {dek && <p className="text-sm text-muted-foreground mb-6 line-clamp-2">
                    {dek}
                  </p>}
                <Link to={post.link || "#"} className="inline-flex items-center gap-2 font-default text-xs tracking-widest uppercase text-black border-b border-black/30 pb-0.5 hover:text-orange-600 hover:border-orange-600 transition-colors duration-300">
                  Baca Selengkapnya <ArrowRight className="size-3" />
                </Link>
              </article>;
        }
        return <article key={post.id} data-index={i} className="mb-10 break-inside-avoid group">
              <Link to={post.link || "#"} className="block">
                <div className={`relative overflow-hidden ${aspect} mb-5 rounded-lg bg-muted`}>
                  {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]" /> : <div className="absolute inset-0 bg-muted" />}
                  <span className="absolute top-4 left-4 font-default text-xs tracking-widest text-white bg-black/70 px-2 py-1 rounded-md">
                    — {indexLabel}
                  </span>
                  <Badge variant="secondary" className="absolute top-4 right-4 font-default text-[10px] tracking-widest uppercase rounded-md bg-white/90 text-black border-0">
                    {categoryName}
                  </Badge>
                </div>
              </Link>

              <p className="font-default text-xs tracking-widest text-muted-foreground mb-3 uppercase">
                {formatEditorialDate(post.date)}
              </p>
              <h3 className="font-default font-semibold text-2xl md:text-[26px] leading-tight text-black mb-2 transition-colors duration-500 group-hover:text-orange-600">
                <Link to={post.link || "#"}>{post.title}</Link>
              </h3>
              {dek && <p className="text-sm text-muted-foreground mb-4 line-clamp-1">
                  {dek}
                </p>}
              <Link to={post.link || "#"} className="inline-flex items-center gap-2 font-default text-xs tracking-widest uppercase text-black border-b border-black/30 pb-0.5 hover:text-orange-600 hover:border-orange-600 transition-colors duration-300">
                Baca Selengkapnya <ArrowRight className="size-3" />
              </Link>
            </article>;
      })}
      </div>

      {totalPages > 1 && <div className="mt-24 pt-10 border-t border-border">
          <Pagination>
            <PaginationContent className="flex w-full items-center justify-between">
              <PaginationItem>
                <Button variant="ghost" onClick={prevPage} disabled={!hasPrev || isRefetching} className="rounded-lg font-default text-xs tracking-widest uppercase text-black hover:bg-orange-50 hover:text-orange-700 disabled:opacity-30">
                  ← Sebelumnya
                </Button>
              </PaginationItem>

              <PaginationItem>
                <span className="font-default text-xs tracking-widest text-muted-foreground uppercase">
                  {String(currentPage).padStart(2, "0")} / {String(totalPages).padStart(2, "0")}
                </span>
              </PaginationItem>

              <PaginationItem>
                <Button variant="ghost" onClick={nextPage} disabled={!hasNext || isRefetching} className="rounded-lg font-default text-xs tracking-widest uppercase text-black hover:bg-orange-50 hover:text-orange-700 disabled:opacity-30">
                  Selanjutnya →
                </Button>
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>}
    </>;
}
export default function RelatedWartaNews() {
  const wp_query_params = {
    post_type: "post",
    per_page: 9,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="4549" id="relatedwartanews" className="bg-white py-[100px] px-6 md:px-12 lg:px-20">
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-20 max-w-3xl">
          <p className="font-default text-xs tracking-widest text-orange-600 uppercase mb-6">
            — ABADI.NEWS
          </p>
          <h2 className="font-default font-bold text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-black tracking-tight">
            Berita Terkini & Pilihan.
          </h2>
          <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
            Pantau informasi nasional dan daerah terbaru yang disajikan secara akurat, tajam, dan terpercaya oleh tim redaksi ABADI.NEWS.
          </p>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 9,
        order: "desc",
        orderby: "date"
      }}>
          <JournalConsumer />
        </PostsProvider>
      </div>
    </section>;
}